import { useState } from "react";
import { mockFactoryNotifications } from "@/mocks/factories";

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function NotificationPanel() {
  const [notifications, setNotifications] = useState(mockFactoryNotifications);
  const [isOpen, setIsOpen] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const markRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, isRead: true } : n));
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="relative flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-notification-3-line text-base" />
        <span className="hidden sm:inline">Notifications</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-orange-500 text-white text-xs font-bold rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-12 z-40 w-96 bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900">Factory Activity</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full">{unreadCount} new</span>
                )}
              </div>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-orange-500 font-semibold hover:text-orange-600 cursor-pointer whitespace-nowrap">
                  Mark all read
                </button>
              )}
            </div>

            {/* Email Alert Banner */}
            <div className="mx-4 mt-3 mb-1 bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-mail-check-line text-green-600 text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700">
                <strong>Email alerts are ON.</strong> You&apos;ll receive an email whenever a factory views your requirement or sends a quotation.
              </p>
            </div>

            {/* Notification List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-3">
                    <i className="ri-notification-off-line text-2xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-400">No notifications yet</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => markRead(notif.id)}
                    className={`flex items-start gap-3 px-5 py-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${!notif.isRead ? "bg-orange-50/40" : ""}`}
                  >
                    {/* Factory Avatar */}
                    <div className="relative flex-shrink-0">
                      <img src={notif.factoryAvatar} alt={notif.factoryName} className="w-10 h-10 rounded-xl object-cover" />
                      {notif.factoryVerified && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 flex items-center justify-center bg-green-500 rounded-full border border-white">
                          <i className="ri-check-line text-white text-xs" style={{ fontSize: "8px" }} />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-gray-900 truncate">{notif.factoryName}</p>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            <i className="ri-file-list-line mr-1" />{notif.requirementTitle}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(notif.viewedAt)}</span>
                          {!notif.isRead && (
                            <span className="w-2 h-2 bg-orange-500 rounded-full" />
                          )}
                        </div>
                      </div>

                      {/* Type Badge */}
                      <div className="mt-2">
                        {notif.type === "factory_viewed" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            <i className="ri-eye-line" /> Factory Viewed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            <i className="ri-mail-send-line" /> Quotation Received
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-400 text-center">
                <i className="ri-mail-line mr-1" />Email alerts sent to your registered email address
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
